import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

const FOCUSES = [
  { key: "summary", label: "Executive Summary" },
  { key: "demand", label: "Demand Forecast" },
  { key: "anomaly", label: "Anomaly Detection" },
  { key: "priority", label: "Priority Recommendations" },
];

export default function AdminAI() {
  const [insights, setInsights] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [active, setActive] = useState(null);

  const refresh = async () => {
    const { data } = await api.get("/ai/insights");
    setInsights(data);
    if (!active && data[0]) setActive(data[0]);
  };
  useEffect(() => { refresh(); }, []);

  const generate = async (focus) => {
    setGenerating(focus);
    try {
      const { data } = await api.post("/ai/insights", { focus });
      setActive(data);
      toast.success(`Generated ${focus} insight`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="bin-label flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-cyan-400" /> AI OPTIMIZATION LAYER</div>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Insights & Recommendations</h1>
        <p className="text-sm text-zinc-500 mt-2">Powered by Claude · Live network state is fed as context.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-4 mb-4">
        {FOCUSES.map((f) => (
          <button
            key={f.key}
            data-testid={`ai-generate-${f.key}`}
            onClick={() => generate(f.key)}
            disabled={generating === f.key}
            className="bin-card p-4 text-left hover:border-cyan-500/50 transition-colors"
          >
            <div className="bin-label mb-1">{f.label}</div>
            <div className="flex items-center gap-1 text-cyan-400 text-xs font-mono">
              {generating === f.key ? <><Loader2 className="w-3 h-3 animate-spin" /> Reasoning…</> : "Generate →"}
            </div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bin-card p-6 min-h-[400px]">
          {active ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="bin-label">{active.focus.toUpperCase()}</div>
                  <div className="text-xs text-zinc-500 font-mono">{new Date(active.created_at).toLocaleString()}</div>
                </div>
                <Sparkles className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-sm leading-relaxed whitespace-pre-wrap text-zinc-200" data-testid="ai-output">
                {active.text}
              </div>
            </>
          ) : (
            <div className="grid place-items-center h-full text-zinc-500 text-sm">
              No insights yet. Generate one above.
            </div>
          )}
        </div>

        <div className="bin-card p-6 max-h-[600px] overflow-auto">
          <div className="bin-label mb-3">HISTORY</div>
          <div className="space-y-2">
            {insights.map((i) => (
              <button
                key={i.id}
                onClick={() => setActive(i)}
                className={`w-full text-left p-3 rounded border ${active?.id === i.id ? "border-cyan-500/50 bg-cyan-500/5" : "border-zinc-800"} hover:border-cyan-500/40`}
              >
                <div className="text-xs font-mono uppercase text-cyan-400">{i.focus}</div>
                <div className="text-xs text-zinc-500 font-mono">{new Date(i.created_at).toLocaleString()}</div>
                <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{i.text.slice(0, 100)}…</div>
              </button>
            ))}
            {insights.length === 0 && <div className="text-xs text-zinc-500">No history yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
